import splitArray from "../../../Preview/Block/BlockType/BlockParts/utils/splitArray";
import horizontalSplit from "../../../Preview/Block/BlockType/BlockParts/utils/horizontalSplit";
import { splitGutter } from "../../../Preview/Block/BlockType/BlockParts/gutterUtils";
import { clampColumnWidthPct } from "../../../utils/maxColumnWidth";
import {
  BULLET_FONT_FAMILY,
  INHERITED_LINE_HEIGHT,
  baselineInLineBox,
  createTextOp,
  layoutParagraph,
  prepareText,
  resolveFont,
  resolveLineHeightPx,
  toPx,
} from "../text";

// Same glyph table as NameOnly2.jsx: size and lift are in name-font ems.
export const BULLET_MARKS = {
  circle: { glyph: "•", fontSize: 1.4138, lift: 0.4953 },
  square: { glyph: "▪", fontSize: 1.5482, lift: 0.5114 },
  diamond: { glyph: "♦", fontSize: 0.8, lift: 0.2164 },
  lineVertical: { glyph: "I", fontSize: 0.8801, lift: 0.315 },
  cross: { glyph: "×", fontSize: 0.8813, fontWeight: 700, lift: 0.3111 },
};
const BULLET_SLOT_EM = 1.2;

const ALIGN_TABLE = {
  left: {
    1: ["left"],
    2: ["left", "left"],
    3: ["left", "left", "left"],
    4: ["left", "left", "left", "left"],
    5: ["left", "left", "left", "left", "left"],
  },
  justify: {
    1: ["left"],
    2: ["left", "right"],
    3: ["left", "center", "right"],
    4: ["left", "left", "right", "right"],
    5: ["left", "left", "center", "right", "right"],
  },
  right: {
    1: ["right"],
    2: ["right", "right"],
    3: ["right", "right", "right"],
    4: ["right", "right", "right", "right"],
    5: ["right", "right", "right", "right", "right"],
  },
  center: {
    1: ["center"],
    2: ["center", "center"],
    3: ["center", "center", "center"],
    4: ["center", "center", "center", "center"],
    5: ["center", "center", "center", "center", "center"],
  },
  default: {
    1: ["center"],
    2: ["right", "left"],
    3: ["right", "center", "left"],
    4: ["right", "right", "left", "left"],
    5: ["right", "right", "center", "left", "left"],
  },
};

export const getNameOnlyColumnAlign = (textAlignment, columns, index) => {
  const cols = Number(columns) || 1;
  const table = ALIGN_TABLE[textAlignment] || ALIGN_TABLE.default;
  const row = table[cols] || table[5];
  // Unlisted columns inherit #preview's text-align: center.
  return row[index] || "center";
};

export const getNameOnlyColumnPadding = (textAlignment, cols, colIdx, gutter) => {
  const cellGutter = splitGutter(gutter);
  let left = 0;
  let right = 0;
  if (textAlignment === "gutter") {
    if (cols === 2) {
      if (colIdx === 0) right = cellGutter;
      if (colIdx === 1) left = cellGutter;
    }
    if (cols === 3 || cols === 5) {
      if (colIdx > 0) left = cellGutter;
      if (colIdx < cols - 1) right = cellGutter;
    }
    if (cols === 4) {
      if (colIdx === 1) right = cellGutter;
      if (colIdx === 2) left = cellGutter;
    }
  }
  return { left, right };
};

const sortNames = (items, order) => {
  const copy = [...items];
  if (order === "ascending") copy.sort((a, b) => a.name.localeCompare(b.name));
  else if (order === "descending") copy.sort((a, b) => b.name.localeCompare(a.name));
  return copy;
};

function layoutColumns(ctx, names, style) {
  const { ops, measurer, options, settings, left, width, top } = ctx;
  const columnCount = Math.max(1, Number(options?.columns) || 1);
  const columnWidth = clampColumnWidthPct(options?.column_width, columnCount);
  const totalWidthPct = Math.min(columnWidth * columnCount, 100);
  const gridWidth = (width * totalWidthPct) / 100;
  const gridLeft = left + (width - gridWidth) / 2;
  const colWidth = gridWidth / columnCount;

  const splitOptions = { columns: columnCount, balance_columns: options?.balance_columns };
  const raw =
    options?.column_flow === "vertical"
      ? splitArray(sortNames(names, options?.column_order), splitOptions)?.ans
      : horizontalSplit(sortNames(names, options?.column_order), splitOptions)?.ans;
  const columns = Array.from({ length: columnCount }, (_, i) => raw?.[i] || []);

  const font = resolveFont(style, { size: 18 });
  const lineHeightPx = resolveLineHeightPx(font, style.lineHeight);
  const marginBottom = toPx(style.marginBottom, 0);

  let height = 0;
  columns.forEach((colItems, index) => {
    const pad = getNameOnlyColumnPadding(
      options?.text_alignment,
      columnCount,
      index,
      settings?.gutter,
    );
    const align = getNameOnlyColumnAlign(options?.text_alignment, columnCount, index);
    const cellLeft = gridLeft + index * colWidth + pad.left;
    const cellWidth = Math.max(0, colWidth - pad.left - pad.right);
    let y = top;
    colItems.forEach((item) => {
      y += layoutParagraph(ops, {
        measurer,
        font,
        text: item?.name,
        left: cellLeft,
        width: cellWidth,
        top: y,
        align,
        lineHeightPx,
        color: style.color,
        decoration: style.textDecoration,
        breakWords: true,
      });
      y += marginBottom;
    });
    height = Math.max(height, y - top);
  });
  return height;
}

function layoutBullets(ctx, names, style) {
  const { ops, measurer, options, left, width, top } = ctx;
  const columnCount = Math.max(1, Number(options?.columns) || 1);
  const columnWidth = clampColumnWidthPct(options?.column_width, columnCount);
  const justify = { left: "left", right: "right", center: "center" }[
    options?.text_alignment
  ] || "center";

  // Pair names keep kerning on, matching html2canvas' fillText output.
  const font = resolveFont(style, { size: 18, kerning: true });
  const em = font.size;
  const lineHeightPx = resolveLineHeightPx(font, toPx(style.lineHeight, NaN) + 0.15);
  const marginBottom = toPx(style.marginBottom, 0);
  const offset = justify === "center" ? 0.6 * em : justify === "right" ? 1.2 * em : 0;

  const boxWidth = (width * columnWidth) / 100;
  const footprintLeft = boxWidth > width ? left : left + (width - boxWidth) / 2;
  const boxLeft = footprintLeft + offset;

  const slot = BULLET_SLOT_EM * em;
  const pairs = names.map((item) => {
    const text = prepareText(item.name, font);
    const textWidth = measurer.width(font, text);
    return { text, textWidth, width: textWidth + slot };
  });

  const rows = [];
  let current = [];
  let currentWidth = 0;
  pairs.forEach((pair) => {
    if (current.length && currentWidth + pair.width > boxWidth + 0.01) {
      rows.push({ pairs: current, width: currentWidth });
      current = [];
      currentWidth = 0;
    }
    current.push(pair);
    currentWidth += pair.width;
  });
  if (current.length) rows.push({ pairs: current, width: currentWidth });

  const mark = BULLET_MARKS[options?.bullets] || BULLET_MARKS.circle;
  const glyphFont = {
    family: BULLET_FONT_FAMILY,
    size: mark.fontSize * em,
    weight: mark.fontWeight || 400,
    italic: false,
    letterSpacing: 0,
    transform: "none",
    kerning: true,
  };
  const glyphWidth = measurer.width(glyphFont, mark.glyph);
  const nameMetrics = measurer.metrics(font);
  const baseOffset = baselineInLineBox(nameMetrics, lineHeightPx);
  const glyphRaise = 0.5 * nameMetrics.cap - mark.lift * em;
  const rowHeight = lineHeightPx + marginBottom;

  rows.forEach((row, rowIndex) => {
    const rowTop = top + rowIndex * rowHeight;
    const baseline = rowTop + baseOffset;
    let x = boxLeft;
    if (row.width <= boxWidth + 0.01) {
      if (justify === "center") x = boxLeft + (boxWidth - row.width) / 2;
      else if (justify === "right") x = boxLeft + boxWidth - row.width;
    }
    row.pairs.forEach((pair, pairIndex) => {
      ops.push(
        createTextOp({
          text: pair.text,
          x,
          baseline,
          font,
          color: style.color,
          decoration: style.textDecoration,
          width: pair.textWidth,
          lineTop: rowTop,
          lineBottom: rowTop + lineHeightPx,
        }),
      );
      if (pairIndex < row.pairs.length - 1) {
        ops.push(
          createTextOp({
            text: mark.glyph,
            x: x + pair.textWidth + (slot - glyphWidth) / 2,
            baseline: baseline - glyphRaise,
            font: glyphFont,
            color: style.color,
            width: glyphWidth,
            lineTop: rowTop,
            lineBottom: rowTop + lineHeightPx,
          }),
        );
      }
      x += pair.width;
    });
  });

  const listHeight = rows.length * rowHeight + marginBottom;
  // The list is an inline-block (vertical-align: top) inside an 18px line.
  const strut = 18 * INHERITED_LINE_HEIGHT;
  return Math.max(strut, listHeight);
}

// NameOnly2.jsx: names in a grid of columns, or a wrapped bullet paragraph.
export function layoutNameOnly(ctx) {
  const style = ctx.fontStyle?.nameOnlyStyle || {};
  const names = (ctx.items || []).filter(
    (item) => item?.name && String(item.name).trim() !== "",
  );
  return ctx.options?.bullets
    ? layoutBullets(ctx, names, style)
    : layoutColumns(ctx, names, style);
}
